import { Box, Card, CardActions, CardContent, Grid } from "@mui/material";
import { withStyles, WithStyles } from "@mui/styles";
import { Alert } from "@mui/lab";
import AuthService, { AuthenticationOptions } from "api/Auth";
import styles from "assets/jss/public-styles";
import { RequestIndicator, SpinnerIndicator } from "components/CallServer";
import RequestErrorDialog from "components/CallServer/RequestErrorDialog";
import { RequestErrorReport } from "components/CallServer/RequestErrorReport";
import MuiRegularButton from "components/MuiExtras/Button/MuiRegularButton";
import MuiCompareTextField from "components/MuiExtras/CustomInput/MuiCompareTextField";
import MuiPasswordTextField from "components/MuiExtras/CustomInput/MuiPasswordTextField";
import MuiMessageBox from "components/MuiExtras/MessageBox/MuiMessageBox";
import { withSnackbar, WithSnackbar } from "components/SnackbarProvider/Snackbar";
import { AccountLoginResult } from "models/ApiEnums";
import * as React from "react";
import { WithTranslation, withTranslation } from "react-i18next";
import { RouteComponentProps, withRouter } from "react-router-dom";
import { RoutePaths } from "routes/RouteConfig";
import Session from "services/Session";
import { AuthHelpers } from "views/Public/AuthHelpers";

/////////////////////////////////////////////////////////////////////////////////////////////////

interface IState {
    loading: boolean;
    password: string;
    comparePass: string;
    validRules: boolean;
    submiting: boolean;
    initError: string | string[];
    postError: any;
    authOptions: AuthenticationOptions;
    lastMessage: string;
    lastError: any;
}

interface IProps {
}

interface IRouteProps {
    key: string;
}

type Props = IProps & RouteComponentProps<IRouteProps> & WithStyles<typeof styles> & WithSnackbar & WithTranslation;

/////////////////////////////////////////////////////////////////////////////////////////////////

class ResetPassword extends React.Component<Props, IState> {
    private authService = new AuthService();
    private passwordTextRef: HTMLElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            loading: true,
            password: null,
            comparePass: null,
            validRules: false,
            submiting: false,
            initError: null,
            postError: null,
            authOptions: null,
            lastMessage: null,
            lastError: null
        };
        Session.clearSession();
    }

    ///
    private res(r: string) {
        return this.props.t("Views.Public.ResetPassword." + r);
    }

    private init() {
        this.authService.checkStampIsValid(this.props.match.params.key)
            .then((valid) => {

                if (valid) {
                    this.authService.getSettings()
                        .then(({ authOptions }) => {
                            var errorMsg = (!authOptions.enablePasswordRecovery) ? this.res("RecoverPasswordNotAvailable") : null;
                            this.setState({
                                authOptions,
                                initError: errorMsg, loading: false
                            });
                        });
                }
                else
                    this.setState({
                        initError: AuthHelpers.translateStatus(AccountLoginResult.SecurityStampNotFound),
                        loading: false
                    });

            })
            .catch((error) => {
                this.setState({
                    initError: error,
                    loading: false
                });
            });
    }

    ///
    private checkForSubmit(): boolean {
        return this.state.password && this.state.comparePass
            && this.state.password == this.state.comparePass
            && this.state.validRules
            && !this.state.submiting;
    }

    ///
    private handleSubmit(event: React.MouseEvent<HTMLButtonElement>) {
        event.preventDefault();

        this.setState({ submiting: true, lastError: null, lastMessage: null });

        this.authService.changePassword(this.props.match.params.key, this.state.password)
            .then((status) => {
                if (status == AccountLoginResult.Valid)
                    this.props.history.replace(RoutePaths.TimelineStartup);
                else if (status == AccountLoginResult.NotAgreement || status == AccountLoginResult.AgreementUpdated)
                    this.props.history.replace(RoutePaths.LoginNotAgree);
                else {
                    var msg = AuthHelpers.translateStatus(status);
                    this.setState({ submiting: false, lastMessage: msg });
                }
            })
            .catch((lastError) => {
                this.setState({ submiting: false, lastError: lastError });
            });
    }

    ///
    private passwordForm() {
        const { classes } = this.props;
        const { submiting, authOptions, lastMessage, lastError } = this.state;

        return (
            <form autoComplete="off" spellCheck={false} >

                <CardContent>

                    <MuiPasswordTextField
                        label={this.res("NewPasswordLabel")}
                        autoFocus={true}
                        fullWidth={true}
                        getInputRef={(ref) => this.passwordTextRef = ref}
                        disabled={this.state.submiting}
                        minLength={authOptions.policies.passwordMinLength}
                        minNumericChars={authOptions.policies.passwordMinNumericChars}
                        minSpecialChars={authOptions.policies.passwordMinSpecialChars}
                        minLowerChars={authOptions.policies.passwordMinLowerChars}
                        minUpperChars={authOptions.policies.passwordMinUpperChars}
                        onChange={(text, isValid) => { this.setState({ submiting: false, password: text, validRules: isValid }); }}
                    />

                    <MuiCompareTextField
                        label={this.res("ConfirmNewPasswordLabel")}
                        fullWidth={true}
                        disabled={this.state.submiting}
                        valueToCompare={this.state.password}
                        onChange={(text) => { this.setState({ submiting: false, comparePass: text }); }}
                    />

                    {
                        lastMessage &&
                        <Box mt={2}>
                            <Alert severity="error">{lastMessage}</Alert>
                        </Box>
                    }

                    {
                        lastError &&
                        <RequestErrorReport error={lastError} />
                    }
                </CardContent>

                <CardActions className={classes.cardFooter}>
                    <MuiRegularButton
                        type="submit"
                        color="primary"
                        size="large"
                        disabled={!this.checkForSubmit()}
                        onClick={(e: React.MouseEvent<HTMLButtonElement>) => this.handleSubmit(e)}
                    >
                        <span>{this.res("ButtonText")}</span>
                        {submiting && <SpinnerIndicator />}
                    </MuiRegularButton>
                </CardActions>
            </form>
        );
    }

    ///
    private setInputFocus() {
        this.passwordTextRef.focus();
    };

    ///
    public componentDidMount(): void {
        this.init();
    }

    ///
    public render() {
        const { initError, loading, postError } = this.state;

        return (
            loading
                ? <RequestIndicator type="waiting" />
                : initError != null
                    ? <MuiMessageBox
                        variant="error"
                        message={initError}
                        action={
                            <MuiRegularButton
                                color="error"
                                size="large"
                                onClick={() => { this.props.history.replace(RoutePaths.TimelineStartup); }
                                }
                            >
                                <span>{this.props.t("Common.Buttons.HomeText")}</span>
                            </MuiRegularButton>
                        } />
                    : (
                        <Grid item style={{ width: "100%" }}>
                            <Card>
                                {this.passwordForm()}

                                <RequestErrorDialog
                                    open={postError != null}
                                    error={postError}
                                    onClose={() => this.setState({ postError: null }, () => this.setInputFocus())}
                                />
                            </Card>
                        </Grid>
                    )
        );
    }
}

export default withRouter(withSnackbar(withTranslation()(withStyles(styles)(ResetPassword))));